'use client'
import type { FC } from 'react'
import { useParams } from 'next/navigation'
import styled from 'styled-components'
import NextImage from 'next/image'
import { useTranslator } from '@/hooks'
import { innerGrid, mq, presetsFluid, rgbAlpha, vw } from '@/styles'
import type { TypeLang } from '@/schema'
import { halfScreenGrid } from './HeaderBrands'
import { Accordion } from '@/components/base'
import { diagonalSvg } from '../layout/AsidePreFooter'

type TypeWarrantyItem = {
  title: string
  description: string
  image_type?: 'jpg' | 'gif'
}

const headingId = 'brand-warranty'
export const SectionBrandWarranty: FC = () => {
  const { slug } = useParams() as { slug: 'cose' | 'softy' | 'ladecor', lang: TypeLang }
  const { t } = useTranslator()
  const warrantyItems = t(`project.brands.${slug}.warranty.list_items`) as unknown as Array<TypeWarrantyItem>

  return (
    <Section aria-labelledby={headingId}>
      <hgroup>
        <h2 id={headingId}>{t(`project.brands.${slug}.warranty.title`)}</h2>
        <p>{t(`project.brands.${slug}.warranty.description`)}</p>
      </hgroup>
      {warrantyItems?.length > 0 && (
        <article>
          <ul>
            {warrantyItems.map((item, index) => (
              <li key={index}>
                <Accordion title={item.title} initialExpanded={index === 0} headingLevel={3} disabled={item.description?.length === 0}>
                  <div className='content-wrapper'>
                    {/* Medium content goes here */}
                    {!!item.image_type ?
                      <div className='medium-wrapper'>
                        {diagonalSvg()}
                        <NextImage src={`/media/brands/${slug}/${index + 1}.${item.image_type}`} alt={item.title} width={800} height={600} />
                      </div>
                    : <></>}
                    <p>{item.description}</p>
                  </div>
                </Accordion>
              </li>
            ))}
          </ul>
        </article>
      )}
    </Section>
  )
}

const Section = styled.section`
  align-items: center;
  background-color: var(--color-background--dark);
  border-top: 1px solid ${rgbAlpha('--color-text--dark', .2)};
  color: var(--color-text--dark);
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-height: 300px;
  width: var(--viewport-width);
  width: var(--viewport-width--dynamic);

  ${mq.greaterThan('tablet')} {
    align-items: flex-start;
    flex-direction: row;
  }

  hgroup,
  article {
    ${mq.greaterThan('tablet')} {
      ${innerGrid(halfScreenGrid.tablet!)};
      width: 50vw !important;
      width: 50dvw !important;
    }

    ${mq.greaterThan('desktop-xs')} {
      width: var(--viewport-width-half);
      width: var(--viewport-width-half--dynamic);
    }

    ${mq.greaterThan('desktop')} {
      ${innerGrid(halfScreenGrid.desktop!)};
    }
  }

  hgroup {
    padding: ${vw(20, 'mobile')} ${vw(10, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      padding: ${vw(20, 'tablet')} ${vw(10, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      height: 100%;
      padding: ${vw(30, 'desktop')} ${vw(35, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      padding: 30px ${vw(30, 'desktop')};
    }

    h2,
    p {
      text-align: left;
    }

    h2 {
      ${presetsFluid.font18()}
      margin-bottom: ${vw(20, 'mobile')};
      opacity: .4;

      ${mq.greaterThan('nexus7')} {
        margin-bottom: ${vw(20, 'tablet')};
      }

      ${mq.greaterThan('tablet')} {
        grid-column: 1 / span 6;
        margin-bottom: ${vw(10, 'desktop')};
      }

      ${mq.greaterThan('desktop')} {
				${presetsFluid.font18_rel()}
        margin-bottom: 10px;
      }
    }

    p {
      ${presetsFluid.font35_50()}
      height: 100%;

      ${mq.greaterThan('tablet')} {
        grid-column: 1 / span 5;
      }

      ${mq.greaterThan('desktop')} {
        ${presetsFluid.font50_rel()}
        font-weight: 400;
      }
    }
  }

  article {
		${mq.greaterThan('tablet')} {
			align-self: stretch;
			border-left: 1px solid ${rgbAlpha('--color-text--dark', .2)};
			height: stretch;
		}
  }

  ul {
    grid-column: 1 / span 6;

    li {
      > div {
        margin-bottom: 0;
      }
    }
  }

  .btn--acc-trigger {
    ${presetsFluid.font20_25()}
    border-top: 1px solid ${rgbAlpha('--color-text--dark', .2)};
    padding: ${vw(20, 'mobile')} ${vw(10, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      padding: ${vw(20, 'tablet')} ${vw(10, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      padding: ${vw(30, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
			${presetsFluid.font25_rel()}
      padding: 30px ${vw(30, 'desktop')};
    }
  }

  .acc__content {
    padding: 0 ${vw(10, 'mobile')} ${vw(30, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      padding: 0 ${vw(10, 'tablet')} ${vw(30, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      padding: 0 ${vw(30, 'desktop')} ${vw(20, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      padding: 0 ${vw(30, 'desktop')} 20px;
    }
  }

	.acc__panel {
		&[aria-hidden='false'] {
			.acc__content {
				padding: 5px ${vw(10, 'mobile')};
	
				${mq.greaterThan('nexus7')} {
					padding: 5px ${vw(10, 'tablet')};
				}
	
				${mq.greaterThan('tablet')} {
					padding: 5px ${vw(30, 'desktop')};
				}
	
				${mq.greaterThan('desktop')} {
					padding: 5px ${vw(30, 'desktop')};
				}
			}
		}
	}

  .medium-wrapper {
    flex-shrink: 0;
    height: ${vw(190, 'mobile')};
    overflow: hidden;
    position: relative;
		margin-left: ${vw(10, 'mobile')};
    margin-right: ${vw(20, 'mobile')};
    width: ${vw(190, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      height: ${vw(190, 'tablet')};
			margin-left: ${vw(10, 'tablet')};
      margin-right: ${vw(20, 'tablet')};
      width: ${vw(190, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      height: ${vw(427, 'desktop')};
			margin-left: 0;
      margin-right: ${vw(30, 'desktop')};
      width: ${vw(427, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      height: 427px;
			margin-left: 0;
      margin-right: 30px;
      width: 427px;
    }

    img,
    video {
      height: 100%;
      object-fit: cover;
      width: 100%;
    }
  }

  .content-wrapper {
    display: flex;
		padding-bottom: ${vw(15, 'mobile')};

		${mq.greaterThan('nexus7')} {
			padding-bottom: ${vw(15, 'tablet')};
		}

		${mq.greaterThan('tablet')} {
			padding-bottom: calc(${vw(25, 'desktop')} + 5px);
			padding-right: 0;
		}

		${mq.greaterThan('desktop')} {
			padding-bottom: 25px;
		}
  }
`
